import { useEffect, useState } from 'react';
import { View, Text, TextInput as RNTextInput } from 'react-native';
import { CurrencyInput } from './CurrencyInput';
import { Button } from './Button';
import { LifeEventCategoryField } from './LifeEventCategoryField';
import { LifeEventTaxFields } from './LifeEventTaxFields';
import { YearReferencePicker } from './YearReferencePicker';
import { RecurringPurchaseFields } from './RecurringPurchaseFields';
import { webPrivacyProps } from '../lib/privacy';
import {
  DEFAULT_PURCHASE_GROWTH_RATE_PERCENT,
  EVENT_DESCRIPTION_PLACEHOLDERS,
  LIFE_EVENT_TYPE_HINTS,
  LIFE_EVENT_TYPE_LABELS,
  getDefaultRecurringEndYear,
  parseAmount,
  parsePercent,
  parseRepeatInterval,
  createYearReferenceState,
  isLifeEventInflowType,
  parseYearReferenceInput,
  type GoalInflowTaxTreatment,
  type GoalType,
  type GoalYearReference,
  type EventYearReferencePerson,
  type LifeEventFormValues,
  type YearReferencePickerValue,
} from '../lib/life-events';

const EVENT_TYPES: { type: GoalType; label: string; hint: string }[] = [
  {
    type: 'expense',
    label: LIFE_EVENT_TYPE_LABELS.expense,
    hint: LIFE_EVENT_TYPE_HINTS.expense,
  },
  {
    type: 'inheritance',
    label: LIFE_EVENT_TYPE_LABELS.inheritance,
    hint: LIFE_EVENT_TYPE_HINTS.inheritance,
  },
];

export interface LifeEventFormProps {
  profiles: EventYearReferencePerson[];
  currentFireYear: number | null | undefined;
  defaultPlanEndYear: number;
  onSave: (values: LifeEventFormValues) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveLabel?: string;
  initialType?: GoalType;
}

export function LifeEventForm({
  profiles,
  currentFireYear,
  defaultPlanEndYear,
  onSave,
  onCancel,
  saving,
  saveLabel = 'Add Event',
  initialType = 'expense',
}: LifeEventFormProps) {
  const currentYear = new Date().getFullYear();
  const [selectedType, setSelectedType] = useState<GoalType>(initialType);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [startYearInput, setStartYearInput] = useState<YearReferencePickerValue>(() =>
    createYearReferenceState(String(currentYear + 5)),
  );
  const [isRecurring, setIsRecurring] = useState(false);
  const [repeatEveryYears, setRepeatEveryYears] = useState('');
  const [endYearInput, setEndYearInput] = useState<YearReferencePickerValue>(() =>
    createYearReferenceState(String(defaultPlanEndYear)),
  );
  const [growthRatePercent, setGrowthRatePercent] = useState(DEFAULT_PURCHASE_GROWTH_RATE_PERCENT);
  const [isTaxable, setIsTaxable] = useState(false);
  const [taxTreatment, setTaxTreatment] =
    useState<GoalInflowTaxTreatment>('ordinary_income');
  // undefined = not chosen yet; null = explicit "no specific category"
  const [budgetCategoryId, setBudgetCategoryId] = useState<
    number | null | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedType(initialType);
    setDescription('');
    setAmount('');
    setStartYearInput(createYearReferenceState(String(currentYear + 5)));
    setIsRecurring(false);
    setRepeatEveryYears('');
    setEndYearInput(createYearReferenceState(String(defaultPlanEndYear)));
    setGrowthRatePercent(DEFAULT_PURCHASE_GROWTH_RATE_PERCENT);
    setIsTaxable(false);
    setTaxTreatment('ordinary_income');
    setBudgetCategoryId(undefined);
    setError(null);
  };

  useEffect(() => {
    setSelectedType(initialType);
    if (initialType !== 'expense') {
      setIsRecurring(false);
    }
  }, [initialType]);

  const handleSave = async () => {
    setError(null);
    const cleanDescription = description.trim();
    const parsedAmount = parseAmount(amount);

    if (!cleanDescription) {
      setError('Description is required.');
      return;
    }
    if (parsedAmount === null) {
      setError('Amount must be a valid number.');
      return;
    }
    if (selectedType === 'expense' && budgetCategoryId === undefined) {
      setError(
        'Choose a spending category for this event (or "No specific category").',
      );
      return;
    }

    const parsedStart = parseYearReferenceInput({
      label: 'Start',
      value: startYearInput,
      fallbackYear: currentYear + 5,
      profiles,
      currentFireYear,
    });
    if (!parsedStart.parsed) {
      setError(parsedStart.error);
      return;
    }

    let repeatInterval: number | undefined;
    let parsedEnd: {
      year: number;
      ref: GoalYearReference;
      personId?: string;
      age?: number;
      resolvedYear: number | null;
    } | null = null;
    let growthRate: number | undefined;

    if (selectedType === 'expense' && isRecurring) {
      const ri = parseRepeatInterval(repeatEveryYears);
      const gr = parsePercent(growthRatePercent);
      if (ri == null) {
        setError('Repeat interval must be at least 1 year.');
        return;
      }
      if (gr == null) {
        setError('Growth rate must be a valid percent.');
        return;
      }

      const nextParsedEnd = parseYearReferenceInput({
        label: 'End',
        value: endYearInput,
        fallbackYear: defaultPlanEndYear,
        profiles,
        currentFireYear,
      });
      if (!nextParsedEnd.parsed) {
        setError(nextParsedEnd.error);
        return;
      }
      if (
        nextParsedEnd.parsed.resolvedYear != null &&
        parsedStart.parsed.resolvedYear != null &&
        nextParsedEnd.parsed.resolvedYear < parsedStart.parsed.resolvedYear
      ) {
        setError('End year must be the same as or after the first year.');
        return;
      }

      repeatInterval = ri;
      parsedEnd = nextParsedEnd.parsed;
      growthRate = gr;
    }

    try {
      await onSave({
        type: selectedType,
        description: cleanDescription,
        amount: parsedAmount,
        year: parsedStart.parsed.year,
        startYearRef: parsedStart.parsed.ref,
        ...(parsedStart.parsed.personId ? { startYearPersonId: parsedStart.parsed.personId } : {}),
        ...(parsedStart.parsed.age != null ? { startYearAge: parsedStart.parsed.age } : {}),
        ...(isLifeEventInflowType(selectedType)
          ? {
              isTaxable,
              taxTreatment: isTaxable ? taxTreatment : null,
            }
          : {}),
        ...(selectedType === 'expense' && budgetCategoryId !== undefined
          ? { budgetCategoryId }
          : {}),
        ...(selectedType === 'expense' && repeatInterval != null
          ? {
              repeatEveryYears: repeatInterval,
              endYear: parsedEnd?.year,
              ...(parsedEnd != null ? { endYearRef: parsedEnd.ref } : {}),
              ...(parsedEnd?.personId ? { endYearPersonId: parsedEnd.personId } : {}),
              ...(parsedEnd?.age != null ? { endYearAge: parsedEnd.age } : {}),
              growthRate,
            }
          : {}),
      });
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save event. Please try again.');
    }
  };

  return (
    <View>
      <View className="flex-row flex-wrap gap-2 mb-3">
        {EVENT_TYPES.map((item) => (
          <Button
            testID={`life-event-type-${item.type}-button`}
            accessibilityLabel={`Select ${item.label} life event`}
            key={item.type}
            size="sm"
            variant={selectedType === item.type ? 'primary' : 'secondary'}
            onPress={() => {
              setSelectedType(item.type);
              setError(null);
              if (item.type !== 'expense') {
                setIsRecurring(false);
              } else {
                setIsTaxable(false);
                setTaxTreatment('ordinary_income');
              }
            }}
          >
            {item.label}
          </Button>
        ))}
      </View>

      <Text className="text-xs text-gray-400 mb-3">
        {EVENT_TYPES.find((item) => item.type === selectedType)?.hint}
      </Text>

      {error && (
        <View className="rounded-xl bg-red-50 dark:bg-red-950 px-3 py-2 mb-3">
          <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
        </View>
      )}

      <RNTextInput
        testID="life-event-description-input"
        accessibilityLabel="Life event description"
        value={description}
        onChangeText={(text) => {
          setDescription(text);
          setError(null);
        }}
        placeholder={EVENT_DESCRIPTION_PLACEHOLDERS[selectedType]}
        className="border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200 mb-2"
        {...webPrivacyProps('sensitive')}
      />

      <View className="mb-3">
        <CurrencyInput
          testID="life-event-amount-input"
          accessibilityLabel="Life event amount"
          value={amount}
          onChangeText={(text) => {
            setAmount(text);
            setError(null);
          }}
          placeholder="Amount (optional)"
          size="sm"
        />
      </View>

      {selectedType === 'expense' && (
        <LifeEventCategoryField
          value={budgetCategoryId}
          onChange={(categoryId) => {
            setBudgetCategoryId(categoryId);
            setError(null);
          }}
        />
      )}

      {isLifeEventInflowType(selectedType) && (
        <LifeEventTaxFields
          isTaxable={isTaxable}
          taxTreatment={taxTreatment}
          onTaxableChange={(value) => {
            setIsTaxable(value);
            setError(null);
          }}
          onTaxTreatmentChange={(value) => {
            setTaxTreatment(value);
            setError(null);
          }}
        />
      )}

      <View className="mb-3">
        <YearReferencePicker
          label={selectedType === 'expense' && isRecurring ? 'Start year' : 'Year'}
          testIDPrefix="life-event-start-year"
          value={startYearInput}
          onChange={(next) => {
            setStartYearInput(next);
            setError(null);
          }}
          profiles={profiles}
          currentFireYear={currentFireYear}
          calendarPlaceholder={selectedType === 'expense' && isRecurring ? 'First year' : 'Year'}
        />
      </View>

      {selectedType === 'expense' && (
        <RecurringPurchaseFields
          isRecurring={isRecurring}
          onSelectOneTime={() => {
            setIsRecurring(false);
            setRepeatEveryYears('');
            setEndYearInput(createYearReferenceState(String(defaultPlanEndYear)));
            setError(null);
          }}
          onSelectRepeats={() => {
            setIsRecurring(true);
            setError(null);
            if (!repeatEveryYears) setRepeatEveryYears('1');
            if (!endYearInput.year) {
              setEndYearInput(
                createYearReferenceState(
                  getDefaultRecurringEndYear(startYearInput.year, defaultPlanEndYear),
                ),
              );
            }
          }}
          repeatEveryYears={repeatEveryYears}
          onRepeatEveryYearsChange={(text) => {
            setRepeatEveryYears(text);
            setError(null);
          }}
          endYear={endYearInput.year}
          onEndYearChange={(text) => {
            setEndYearInput((current) => ({ ...current, year: text }));
            setError(null);
          }}
          growthRatePercent={growthRatePercent}
          onGrowthRatePercentChange={(text) => {
            setGrowthRatePercent(text);
            setError(null);
          }}
          showEndYearInput={false}
        />
      )}

      {selectedType === 'expense' && isRecurring && (
        <View className="mb-3">
          <YearReferencePicker
            label="End year"
            testIDPrefix="life-event-end-year"
            value={endYearInput}
            onChange={(next) => {
              setEndYearInput(next);
              setError(null);
            }}
            profiles={profiles}
            currentFireYear={currentFireYear}
            calendarPlaceholder="End year"
            agePlaceholder="End age"
          />
        </View>
      )}

      <View className="flex-row gap-2">
        <Button
          testID="life-event-save-button"
          accessibilityLabel="Save life event"
          className="flex-1"
          onPress={handleSave}
          loading={saving}
        >
          {saveLabel}
        </Button>
        <Button
          testID="life-event-cancel-button"
          accessibilityLabel="Cancel life event"
          variant="ghost"
          onPress={onCancel}
        >
          Cancel
        </Button>
      </View>
    </View>
  );
}
